import { Injectable } from '@angular/core';
import { CsHttpClientService } from '@cbuild/cs-common-data-services';
import { interval, Observable } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs';

export function getPoolingNameAlreadyExistsError(name: string | number): Error {
  return new Error(`name already exists ${name}`);
}

export function getPoolingNameDoesNotExistsError(name: string | number): Error {
  return new Error(`name doesn't exists ${name}`);
}

export class HttpPooling {
  public processing: boolean;

  constructor(private timeout: number, public url: string) {}

  public attach(): Observable<number> {
    return interval(this.timeout).pipe(startWith(0));
  }
}

@Injectable({
  providedIn: 'root'
})
export class HttpPoolingService {
  private map = new Map<string | number, HttpPooling>();

  constructor(private http: CsHttpClientService) {}

  public attachGet<T>(
    name: string | number,
    timeout: number,
    url: string
  ): Observable<T> {
    if (!this.map.has(name)) {
      const pooling = new HttpPooling(timeout, url);
      this.map.set(name, pooling);
      return pooling.attach().pipe(
        map(() => pooling),
        filter(_ => !_.processing),
        switchMap(poll => {
          poll.processing = true;
          return this.http
            .read<T>(url)
            .pipe(tap(() => (poll.processing = false)));
        })
      );
    } else {
      throw getPoolingNameAlreadyExistsError(name);
    }
  }

  public attachPost<T, D>(
    name: string | number,
    timeout: number,
    url: string,
    data: D
  ): Observable<T> {
    if (this.map.has(name)) {
      throw getPoolingNameAlreadyExistsError(name);
    }
    const pooling = new HttpPooling(timeout, url);
    this.map.set(name, pooling);
    return pooling.attach().pipe(
      map(() => pooling),
      filter(_ => !_.processing),
      switchMap(poll => {
        poll.processing = true;
        return this.http
          .writeText<T>(url, data)
          .pipe(tap(() => (poll.processing = false)));
      })
    );
  }

  public detach(name: string | number): void {
    if (this.map.has(name)) {
      this.map.delete(name);
    } else {
      throw getPoolingNameDoesNotExistsError(name);
    }
  }
}
